import { useState, useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ShoppingBag,
  X,
  Search,
  ImageIcon,
  Check,
  RefreshCw,
  AlertCircle,
  Sparkles,
  Star,
  Trash2,
  Plus,
  Eye,
  Globe,
} from 'lucide-react';
import {
  createWooDraftFromEnrichment,
  fetchSalesStatsBrands,
  fetchSalesStatsCategories,
  liveLookupEnrichment,
  toTitleCase,
  type CreateWooDraftInput,
  type CreateWooDraftResult,
} from '../api.js';
import { Badge, Button, Input } from '../ui/index.js';

/**
 * Single shared WooCommerce-draft modal (enrichment S21, market discovery,
 * barcode checker). One modal, zero drift: source pills, title-case
 * names, packshot picker with measured dimensions, main + ordered gallery,
 * category hint, brand/category autocomplete.
 */

export interface WooDraftSourceOption {
  readonly source: string;
  readonly value: string;
}

export interface WooDraftImageOption {
  readonly source: string;
  readonly url: string;
}

export interface CreateWooDraftInitialData {
  readonly name?: string;
  readonly ean?: string;
  readonly brand?: string;
  readonly category?: string;
  readonly categoryHint?: string | null;
  readonly contenance?: string;
  readonly regularPrice?: string | number;
  readonly sku?: string;
  readonly shortDescription?: string;
  readonly description?: string;
  readonly imageUrl?: string;
  readonly galleryImages?: readonly string[];
  readonly nameOptions?: readonly WooDraftSourceOption[];
  readonly shortOptions?: readonly WooDraftSourceOption[];
  readonly longOptions?: readonly WooDraftSourceOption[];
  readonly imageOptions?: readonly WooDraftImageOption[];
  readonly proposalId?: number;
  readonly productId?: number;
  readonly offerId?: number;
}

/** Extra source options loaded after open (e.g. sibling fiches). */
export interface WooDraftExtraOptions {
  readonly nameOptions?: readonly WooDraftSourceOption[];
  readonly shortOptions?: readonly WooDraftSourceOption[];
  readonly longOptions?: readonly WooDraftSourceOption[];
  readonly imageOptions?: readonly WooDraftImageOption[];
  readonly categoryHint?: string | null;
}

export interface CreateWooDraftModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly initialData?: CreateWooDraftInitialData | null;
  readonly subtitle?: string | undefined;
  /** Hide the contenance field (its value still flows silently). */
  readonly hideContenanceField?: boolean;
  readonly loadMoreOptions?: (() => Promise<WooDraftExtraOptions | null>) | undefined;
  readonly onSuccess?: (result: CreateWooDraftResult) => void;
}

interface DraftImageItem {
  readonly id: string;
  readonly url: string;
  readonly isMain: boolean;
}

/** Minimum packshot dimension (px) for the green badge. */
const MIN_IMAGE_PX = 1000;

function uniqTextOptions(options: readonly WooDraftSourceOption[]): WooDraftSourceOption[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    const key = o.value.trim();
    if (key === '' || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqImageOptions(options: readonly WooDraftImageOption[]): WooDraftImageOption[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    const key = o.url.trim();
    if (key === '' || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Plain text for pill hover previews. */
function stripTags(text: string): string {
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const DESC_ALLOWED_TAGS = new Set([
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
]);

function smartExcerpt(text: string, maxLen = 400): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLen) return clean;
  const slice = clean.slice(0, maxLen);
  let cut = -1;
  for (const m of slice.matchAll(/[.!?…](?=\s|$)/g)) cut = (m.index ?? -1) + 1;
  if (cut >= Math.floor(maxLen * 0.6)) return slice.slice(0, cut).trim();
  const space = slice.lastIndexOf(' ');
  const hard = space > maxLen * 0.6 ? slice.slice(0, space) : slice;
  return `${hard.trim()} …`;
}

function sanitizeDescriptionHtml(raw: string): string {
  const input = raw.replace(/\r\n?/g, '\n');
  if (!/<[a-zA-Z][^>]*>/.test(input)) {
    const escaped = input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped
      .split(/\n\s*\n/)
      .map((para) => para.trim())
      .filter((para) => para !== '')
      .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('\n');
  }
  const noJunk = input
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/&nbsp;/gi, ' ');
  const headingsToBold = noJunk
    .replace(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi, '<p><strong>$1</strong></p>')
    .replace(/<h[1-6]\b[^>]*>/gi, '<p><strong>')
    .replace(/<\/h[1-6]>/gi, '</strong></p>');
  const filtered = headingsToBold.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (m: string, tag: string) => {
    const t = tag.toLowerCase();
    if (!DESC_ALLOWED_TAGS.has(t)) return '';
    if (m.startsWith('</')) return `</${t}>`;
    return t === 'br' ? '<br>' : `<${t}>`;
  });
  return filtered
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function richExcerpt(sanitizedHtml: string, maxLen = 400): string {
  const blocks = [...sanitizedHtml.matchAll(/<(p|li|blockquote)>([\s\S]*?)<\/\1>/gi)].map(
    (m) => m[0],
  );
  if (blocks.length === 0) {
    const plain = stripTags(sanitizedHtml);
    return plain === '' ? '' : `<p>${smartExcerpt(plain, maxLen)}</p>`;
  }
  const kept: string[] = [];
  let len = 0;
  for (const block of blocks) {
    const blockLen = stripTags(block).length;
    if (kept.length > 0 && len + blockLen > maxLen) break;
    kept.push(block.trim());
    len += blockLen;
  }
  if (kept.length === 0) {
    return `<p>${smartExcerpt(stripTags(blocks[0] ?? ''), maxLen)}</p>`;
  }
  return kept.join('\n');
}

function richShort(description: string | null, fallback: string | null): string {
  if (description === null || description.trim() === '') return fallback ?? '';
  const out = richExcerpt(sanitizeDescriptionHtml(description), 400);
  if (out !== '') return out;
  return fallback ?? '';
}

export function CreateWooDraftModal({
  isOpen,
  onClose,
  initialData,
  subtitle,
  hideContenanceField = false,
  loadMoreOptions,
  onSuccess,
}: CreateWooDraftModalProps) {
  const [form, setForm] = useState({
    name: '',
    ean: '',
    brand: '',
    category: '',
    categoryHint: null as string | null,
    contenance: '',
    regularPrice: '',
    sku: '',
    shortDescription: '',
    description: '',
    imageUrl: '',
    images: [] as readonly DraftImageItem[],
    newImageUrl: '',
    nameOptions: [] as readonly WooDraftSourceOption[],
    shortOptions: [] as readonly WooDraftSourceOption[],
    longOptions: [] as readonly WooDraftSourceOption[],
    imageOptions: [] as readonly WooDraftImageOption[],
    proposalId: undefined as number | undefined,
    productId: undefined as number | undefined,
    offerId: undefined as number | undefined,
  });

  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageDims, setImageDims] = useState<Record<string, { w: number; h: number } | 'error'>>({});
  const [fetchingSource, setFetchingSource] = useState<string | null>(null);
  const [fetchMessage, setFetchMessage] = useState<string | null>(null);

  const handleGetDrakkarsShort = async () => {
    // 1. If already present in shortOptions, switch immediately
    const existing = form.shortOptions.find((o) => /drakkar/i.test(o.source));
    if (existing && existing.value.trim() !== '') {
      setForm((prev) => ({ ...prev, shortDescription: existing.value }));
      return;
    }
    const existingLong = form.longOptions.find((o) => /drakkar/i.test(o.source));
    if (existingLong && existingLong.value.trim() !== '') {
      const extracted = richShort(existingLong.value, form.name);
      setForm((prev) => ({
        ...prev,
        shortDescription: extracted,
        shortOptions: uniqTextOptions([...prev.shortOptions, { source: 'Pharmacie des Drakkars (FR)', value: extracted }]),
      }));
      return;
    }

    // 2. Fetch live from Drakkars
    const query = form.ean || form.name;
    if (!query) {
      alert('Veuillez renseigner un code EAN ou un nom de produit pour rechercher sur Drakkars.');
      return;
    }
    setFetchingSource('drakkars-short');
    setFetchMessage('Recherche en direct sur Pharmacie des Drakkars…');
    try {
      const res = await liveLookupEnrichment(query, 'pharmaciedesdrakkars', form.productId);
      const rec =
        res.unlinkedRecords.find((r) => r.sourceKey === 'pharmaciedesdrakkars') ??
        res.savedProposals.find((p) => p.sourceKey === 'pharmaciedesdrakkars') ??
        res.unlinkedRecords[0] ??
        res.savedProposals[0];
      const desc = rec?.description ?? null;
      const shortDesc = rec?.shortDescription ?? (desc && desc.trim() !== '' ? richShort(desc, form.name) : null);
      if (rec && (shortDesc || desc)) {
        const extracted = shortDesc ? sanitizeDescriptionHtml(shortDesc) : richShort(desc ?? '', form.name);
        const sanitizedLong = desc ? sanitizeDescriptionHtml(desc) : '';
        setForm((prev) => ({
          ...prev,
          shortDescription: extracted,
          shortOptions: uniqTextOptions([
            ...prev.shortOptions,
            { source: 'Pharmacie des Drakkars (FR)', value: extracted },
          ]),
          longOptions: uniqTextOptions([
            ...prev.longOptions,
            { source: 'Pharmacie des Drakkars (FR)', value: sanitizedLong },
          ]),
          imageOptions: uniqImageOptions([
            ...prev.imageOptions,
            ...(rec.imageUrl ? [{ source: 'Pharmacie des Drakkars (FR)', url: rec.imageUrl }] : []),
            ...(rec.images ? rec.images.map((u) => ({ source: 'Pharmacie des Drakkars (FR)', url: u })) : []),
          ]),
        }));
        setFetchMessage('Description courte Pharmacie des Drakkars récupérée avec succès !');
        setTimeout(() => setFetchMessage(null), 3500);
      } else {
        alert('Aucune description trouvée sur Pharmacie des Drakkars pour ce produit.');
        setFetchMessage(null);
      }
    } catch (err: unknown) {
      alert(`Erreur lors de la récupération Drakkars : ${err instanceof Error ? err.message : String(err)}`);
      setFetchMessage(null);
    } finally {
      setFetchingSource(null);
    }
  };

  const handleGetCocoonLong = async () => {
    // 1. If already present in longOptions and is rich, switch immediately
    const existing = form.longOptions.find((o) => /cocoon/i.test(o.source));
    if (existing && existing.value.trim().length > 250) {
      setForm((prev) => ({ ...prev, description: existing.value }));
      return;
    }

    // 2. Fetch live from Cocoon Center
    const query = form.ean || form.name;
    if (!query) {
      alert('Veuillez renseigner un code EAN ou un nom de produit pour rechercher sur Cocoon Center.');
      return;
    }
    setFetchingSource('cocoon-long');
    setFetchMessage('Recherche en direct sur Cocoon Center…');
    try {
      const res = await liveLookupEnrichment(query, 'cocooncenter', form.productId);
      const rec =
        res.unlinkedRecords.find((r) => r.sourceKey === 'cocooncenter') ??
        res.savedProposals.find((p) => p.sourceKey === 'cocooncenter') ??
        res.unlinkedRecords[0] ??
        res.savedProposals[0];
      const desc = rec?.description ?? null;
      if (rec && desc && desc.trim() !== '') {
        const sanitized = sanitizeDescriptionHtml(desc);
        const shortDesc = rec.shortDescription ?? richShort(desc, form.name);
        setForm((prev) => ({
          ...prev,
          description: sanitized,
          shortDescription: prev.shortDescription ? prev.shortDescription : shortDesc,
          longOptions: uniqTextOptions([
            ...prev.longOptions.filter((o) => !/cocoon/i.test(o.source)),
            { source: 'Cocoon Center (FR)', value: sanitized },
          ]),
          shortOptions: uniqTextOptions([
            ...prev.shortOptions.filter((o) => !/cocoon/i.test(o.source)),
            { source: 'Cocoon Center (FR)', value: shortDesc },
          ]),
          imageOptions: uniqImageOptions([
            ...prev.imageOptions,
            ...(rec.imageUrl ? [{ source: 'Cocoon Center (FR)', url: rec.imageUrl }] : []),
            ...(rec.images ? rec.images.map((u) => ({ source: 'Cocoon Center (FR)', url: u })) : []),
          ]),
        }));
        setFetchMessage('Description complète Cocoon Center récupérée avec succès !');
        setTimeout(() => setFetchMessage(null), 3500);
      } else {
        alert('Aucune description trouvée sur Cocoon Center pour ce produit.');
        setFetchMessage(null);
      }
    } catch (err: unknown) {
      alert(`Erreur lors de la récupération Cocoon Center : ${err instanceof Error ? err.message : String(err)}`);
      setFetchMessage(null);
    } finally {
      setFetchingSource(null);
    }
  };

  const handleGetBoticinalLong = async () => {
    // 1. If already present in longOptions and is rich, switch immediately
    const existing = form.longOptions.find((o) => /boticinal/i.test(o.source));
    if (existing && existing.value.trim().length > 250) {
      setForm((prev) => ({ ...prev, description: existing.value }));
      return;
    }

    // 2. Fetch live from Boticinal
    const query = form.ean || form.name;
    if (!query) {
      alert('Veuillez renseigner un code EAN ou un nom de produit pour rechercher sur Boticinal.');
      return;
    }
    setFetchingSource('boticinal-long');
    setFetchMessage('Recherche en direct sur Boticinal…');
    try {
      const res = await liveLookupEnrichment(query, 'boticinal', form.productId);
      const rec =
        res.unlinkedRecords.find((r) => r.sourceKey === 'boticinal') ??
        res.savedProposals.find((p) => p.sourceKey === 'boticinal') ??
        res.unlinkedRecords[0] ??
        res.savedProposals[0];
      const desc = rec?.description ?? null;
      if (rec && desc && desc.trim() !== '') {
        const sanitized = sanitizeDescriptionHtml(desc);
        const shortDesc = rec.shortDescription ?? richShort(desc, form.name);
        setForm((prev) => ({
          ...prev,
          description: sanitized,
          shortDescription: prev.shortDescription ? prev.shortDescription : shortDesc,
          longOptions: uniqTextOptions([
            ...prev.longOptions.filter((o) => !/boticinal/i.test(o.source)),
            { source: 'Boticinal (FR)', value: sanitized },
          ]),
          shortOptions: uniqTextOptions([
            ...prev.shortOptions.filter((o) => !/boticinal/i.test(o.source)),
            { source: 'Boticinal (FR)', value: shortDesc },
          ]),
          imageOptions: uniqImageOptions([
            ...prev.imageOptions,
            ...(rec.imageUrl ? [{ source: 'Boticinal (FR)', url: rec.imageUrl }] : []),
            ...(rec.images ? rec.images.map((u) => ({ source: 'Boticinal (FR)', url: u })) : []),
          ]),
        }));
        setFetchMessage('Description complète Boticinal (avec Description détaillée, Composition & Conseils) récupérée avec succès !');
        setTimeout(() => setFetchMessage(null), 3500);
      } else {
        alert('Aucune description trouvée sur Boticinal pour ce produit.');
        setFetchMessage(null);
      }
    } catch (err: unknown) {
      alert(`Erreur lors de la récupération Boticinal : ${err instanceof Error ? err.message : String(err)}`);
      setFetchMessage(null);
    } finally {
      setFetchingSource(null);
    }
  };

  const handleGetPharmaGddLong = async () => {
    // 1. If already present in longOptions and is rich, switch immediately
    const existing = form.longOptions.find((o) => /pharma\s*gdd/i.test(o.source));
    if (existing && existing.value.trim().length > 250) {
      setForm((prev) => ({ ...prev, description: existing.value }));
      return;
    }

    // 2. Fetch live from Pharma GDD
    const query = form.ean || form.name;
    if (!query) {
      alert('Veuillez renseigner un code EAN ou un nom de produit pour rechercher sur Pharma GDD.');
      return;
    }
    setFetchingSource('pharmagdd-long');
    setFetchMessage('Recherche en direct sur Pharma GDD…');
    try {
      const res = await liveLookupEnrichment(query, 'pharmagdd', form.productId);
      const rec =
        res.unlinkedRecords.find((r) => r.sourceKey === 'pharmagdd') ??
        res.savedProposals.find((p) => p.sourceKey === 'pharmagdd') ??
        res.unlinkedRecords[0] ??
        res.savedProposals[0];
      const desc = rec?.description ?? null;
      if (rec && desc && desc.trim() !== '') {
        const sanitized = sanitizeDescriptionHtml(desc);
        const shortDesc = rec.shortDescription ?? richShort(desc, form.name);
        setForm((prev) => ({
          ...prev,
          description: sanitized,
          shortDescription: prev.shortDescription ? prev.shortDescription : shortDesc,
          longOptions: uniqTextOptions([
            ...prev.longOptions.filter((o) => !/pharma\s*gdd/i.test(o.source)),
            { source: 'Pharma GDD (FR)', value: sanitized },
          ]),
          shortOptions: uniqTextOptions([
            ...prev.shortOptions.filter((o) => !/pharma\s*gdd/i.test(o.source)),
            { source: 'Pharma GDD (FR)', value: shortDesc },
          ]),
          imageOptions: uniqImageOptions([
            ...prev.imageOptions,
            ...(rec.imageUrl ? [{ source: 'Pharma GDD (FR)', url: rec.imageUrl }] : []),
            ...(rec.images ? rec.images.map((u) => ({ source: 'Pharma GDD (FR)', url: u })) : []),
          ]),
        }));
        setFetchMessage('Description complète Pharma GDD (avec Conseils d\'utilisation & Composition) récupérée avec succès !');
        setTimeout(() => setFetchMessage(null), 3500);
      } else {
        alert('Aucune description trouvée sur Pharma GDD pour ce produit.');
        setFetchMessage(null);
      }
    } catch (err: unknown) {
      alert(`Erreur lors de la récupération Pharma GDD : ${err instanceof Error ? err.message : String(err)}`);
      setFetchMessage(null);
    } finally {
      setFetchingSource(null);
    }
  };

  const loaderRef = useRef(loadMoreOptions);
  loaderRef.current = loadMoreOptions;

  const brandPoolQuery = useQuery({
    queryKey: ['draft-brand-pool'],
    queryFn: fetchSalesStatsBrands,
    staleTime: 10 * 60_000,
  });

  const categoryPoolQuery = useQuery({
    queryKey: ['draft-category-pool'],
    queryFn: fetchSalesStatsCategories,
    staleTime: 10 * 60_000,
  });

  // Populate the form once per opening (transition-guarded, so background
  // parent re-renders — e.g. an inline `initialData` literal — never wipe
  // fields the operator is already editing), then merge async extras.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen || wasOpenRef.current) {
      wasOpenRef.current = isOpen;
      return;
    }
    wasOpenRef.current = true;
    const name = toTitleCase(initialData?.name ?? '');
    const ean = initialData?.ean ? initialData.ean.replace(/\D/g, '') : '';
    const mainImgUrl = initialData?.imageUrl?.trim() ?? '';
    const gallery = (initialData?.galleryImages ?? []).map((u) => u.trim()).filter(Boolean);

    const initialImages: DraftImageItem[] = [];
    if (mainImgUrl) {
      initialImages.push({ id: `main-${Date.now()}`, url: mainImgUrl, isMain: true });
    }
    for (let i = 0; i < gallery.length; i++) {
      const gUrl = gallery[i]!;
      if (!initialImages.some((img) => img.url === gUrl)) {
        initialImages.push({
          id: `gallery-${i}-${Date.now()}`,
          url: gUrl,
          isMain: initialImages.length === 0,
        });
      }
    }

    const brand = initialData?.brand ? toTitleCase(initialData.brand) : '';
    const contenance = initialData?.contenance ?? '';
    const defaultShort =
      initialData?.shortDescription ??
      (name
        ? `<p><strong>${name}</strong>${brand ? ` - ${brand}` : ''}${contenance ? ` (${contenance})` : ''}.${ean ? ` Code-barres EAN : ${ean}.` : ''}</p>`
        : '');
    const defaultLong =
      initialData?.description ??
      (name
        ? `<p><strong>${name}</strong>${brand ? ` de la marque ${brand}` : ''}${contenance ? ` (${contenance})` : ''}.</p>\n` +
          `<p><strong>Caractéristiques du produit :</strong></p>\n<ul>\n` +
          (brand ? `  <li><strong>Marque :</strong> ${brand}</li>\n` : '') +
          (contenance ? `  <li><strong>Contenance :</strong> ${contenance}</li>\n` : '') +
          (ean ? `  <li><strong>Code-barres (EAN) :</strong> ${ean}</li>\n` : '') +
          `</ul>`
        : '');

    setForm({
      name,
      ean,
      brand,
      category: initialData?.category ?? '',
      categoryHint: initialData?.categoryHint ?? null,
      contenance,
      regularPrice:
        initialData?.regularPrice !== undefined && initialData?.regularPrice !== null
          ? String(initialData.regularPrice)
          : '',
      sku: initialData?.sku ?? '',
      shortDescription: defaultShort,
      description: defaultLong,
      imageUrl: mainImgUrl,
      images: initialImages,
      newImageUrl: '',
      nameOptions: uniqTextOptions(initialData?.nameOptions ?? []),
      shortOptions: uniqTextOptions(initialData?.shortOptions ?? []),
      longOptions: uniqTextOptions(initialData?.longOptions ?? []),
      imageOptions: uniqImageOptions([
        ...(mainImgUrl !== '' ? [{ source: 'Fiche', url: mainImgUrl }] : []),
        ...(initialData?.imageOptions ?? []),
      ]),
      proposalId: initialData?.proposalId,
      productId: initialData?.productId,
      offerId: initialData?.offerId,
    });
    setImagePickerOpen(false);

    let cancelled = false;
    const loader = loaderRef.current;
    if (loader) {
      void loader().then((extra) => {
        if (cancelled || !extra) return;
        setForm((prev) => ({
          ...prev,
          nameOptions: uniqTextOptions([...prev.nameOptions, ...(extra.nameOptions ?? [])]),
          shortOptions: uniqTextOptions([...prev.shortOptions, ...(extra.shortOptions ?? [])]),
          longOptions: uniqTextOptions([...prev.longOptions, ...(extra.longOptions ?? [])]),
          imageOptions: uniqImageOptions([...prev.imageOptions, ...(extra.imageOptions ?? [])]),
          categoryHint: prev.categoryHint ?? extra.categoryHint ?? null,
        }));
      });
    }
    return () => {
      cancelled = true;
    };
    // NOTE: intentionally keyed on open-transition via wasOpenRef above, not
    // on initialData identity (parents may pass inline literals).
  });

  // Probe candidate image dimensions (plain <img> needs no CORS).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const urls = new Set<string>();
    for (const img of form.images) urls.add(img.url);
    for (const opt of form.imageOptions) urls.add(opt.url);
    if (form.newImageUrl.trim() !== '') urls.add(form.newImageUrl.trim());
    for (const url of urls) {
      if (imageDims[url] !== undefined) continue;
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        setImageDims((prev) =>
          prev[url] === undefined ? { ...prev, [url]: { w: probe.naturalWidth, h: probe.naturalHeight } } : prev,
        );
      };
      probe.onerror = () => {
        if (cancelled) return;
        setImageDims((prev) => (prev[url] === undefined ? { ...prev, [url]: 'error' } : prev));
      };
      probe.src = url;
    }
    return () => {
      cancelled = true;
    };
  }, [isOpen, form.images, form.imageOptions, form.newImageUrl, imageDims]);

  const handleAddImage = (urlToAdd: string) => {
    const trimmed = urlToAdd.trim();
    if (!trimmed) return;
    if (form.images.some((img) => img.url === trimmed)) {
      setForm((prev) => ({ ...prev, newImageUrl: '' }));
      return;
    }
    const isFirst = form.images.length === 0;
    setForm((prev) => ({
      ...prev,
      images: [
        ...prev.images,
        {
          id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          url: trimmed,
          isMain: isFirst,
        },
      ],
      newImageUrl: '',
    }));
  };

  const ensureImage = (url: string): string | null => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    const existing = form.images.find((img) => img.url === trimmed);
    if (existing) return existing.id;
    const id = `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setForm((prev) => ({
      ...prev,
      images: [...prev.images, { id, url: trimmed, isMain: prev.images.length === 0 }],
    }));
    return id;
  };

  const handleSetMainImage = (id: string) => {
    setForm((prev) => {
      const target = prev.images.find((img) => img.id === id);
      return {
        ...prev,
        imageUrl: target?.url ?? prev.imageUrl,
        images: prev.images.map((img) => ({
          ...img,
          isMain: img.id === id,
        })),
      };
    });
  };

  const handleRemoveImage = (id: string) => {
    setForm((prev) => {
      const remaining = prev.images.filter((img) => img.id !== id);
      const newMain = remaining.find((img) => img.isMain) ?? remaining[0];
      if (remaining.length > 0 && !remaining.some((img) => img.isMain)) {
        return {
          ...prev,
          imageUrl: remaining[0]?.url ?? '',
          images: remaining.map((img, idx) => (idx === 0 ? { ...img, isMain: true } : img)),
        };
      }
      return {
        ...prev,
        imageUrl: newMain?.url ?? '',
        images: remaining,
      };
    });
  };

  const createDraftMutation = useMutation({
    mutationFn: (input: CreateWooDraftInput) => createWooDraftFromEnrichment(input),
    onSuccess: (data) => {
      onSuccess?.(data);
      onClose();
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (imagePickerOpen) {
          setImagePickerOpen(false);
          return;
        }
        if (!createDraftMutation.isPending) onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, imagePickerOpen, createDraftMutation.isPending, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const mainImg = form.images.find((img) => img.isMain) ?? form.images[0];
  const dimBadge = (url: string): ReactNode => {
    const d = imageDims[url];
    if (d === undefined)
      return (
        <span className="mt-0.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-ink-ghost">
          mesure…
        </span>
      );
    if (d === 'error')
      return (
        <span className="mt-0.5 inline-block rounded-full bg-rose-100 border border-rose-300 px-2 py-0.5 font-mono text-[11px] font-bold text-rose-800">
          inaccessible
        </span>
      );
    const pass = d.w >= MIN_IMAGE_PX && d.h >= MIN_IMAGE_PX;
    return (
      <span
        className={
          pass
            ? 'mt-0.5 inline-block rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 font-mono text-[11px] font-bold text-emerald-900'
            : 'mt-0.5 inline-block rounded-full bg-rose-100 border border-rose-300 px-2 py-0.5 font-mono text-[11px] font-bold text-rose-800'
        }
      >
        {d.w} × {d.h}
        {pass ? ' ✓' : ` — < ${MIN_IMAGE_PX} px`}
      </span>
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Veuillez renseigner le nom du produit.');
      return;
    }
    const orderedImages =
      form.images.length > 0
        ? [
            ...(mainImg ? [{ src: mainImg.url, isMain: true }] : []),
            ...form.images.filter((img) => img !== mainImg).map((img) => ({ src: img.url, isMain: false })),
          ]
        : [];

    createDraftMutation.mutate({
      name: form.name.trim(),
      ean: form.ean.trim() || undefined,
      brand: form.brand.trim() || undefined,
      category: form.category.trim() || undefined,
      contenance: form.contenance.trim() || undefined,
      regularPrice: form.regularPrice.trim() || undefined,
      sku: form.sku.trim() || undefined,
      shortDescription: form.shortDescription.trim() || undefined,
      description: form.description.trim() || undefined,
      imageUrl: mainImg?.url,
      images: orderedImages,
      proposalId: form.proposalId,
      productId: form.productId,
      offerId: form.offerId,
    });
  };

  const matchedBrands =
    form.brand.trim().length >= 2
      ? (brandPoolQuery.data?.brands ?? [])
          .filter(
            (b) =>
              b.brand.toLowerCase().includes(form.brand.trim().toLowerCase()) &&
              b.brand.toLowerCase() !== form.brand.trim().toLowerCase(),
          )
          .slice(0, 8)
      : [];

  const matchedCategories =
    form.category.trim().length >= 2
      ? (categoryPoolQuery.data?.categories ?? [])
          .filter(
            (c) =>
              c.name.toLowerCase().includes(form.category.trim().toLowerCase()) &&
              c.name.toLowerCase() !== form.category.trim().toLowerCase(),
          )
          .slice(0, 8)
      : [];

  const sourcePills = (
    options: readonly WooDraftSourceOption[],
    current: string,
    onPick: (value: string) => void,
    previewPlain = false,
  ): ReactNode => {
    const distinct = uniqTextOptions(options);
    if (distinct.length < 2) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {distinct.map((o) => {
          const active = o.value === current;
          const full = o.value.length > 90 ? `${o.value.slice(0, 90)}…` : o.value;
          return (
            <button
              key={`${o.source}:${o.value.slice(0, 40)}`}
              type="button"
              onClick={() => onPick(o.value)}
              title={previewPlain ? stripTags(full) : full}
              className={
                active
                  ? 'rounded-full bg-herb-700 text-white px-2.5 py-1 text-[11px] font-bold shadow-2xs cursor-pointer'
                  : 'rounded-full border border-herb-600/30 bg-herb-50/60 text-herb-900 px-2.5 py-1 text-[11px] font-semibold hover:bg-herb-100 transition-colors cursor-pointer'
              }
            >
              {o.source}
            </button>
          );
        })}
      </div>
    );
  };

  const galleryOrder = (url: string): number =>
    form.images.find((img) => !img.isMain && img.url === url)
      ? form.images.filter((img) => !img.isMain).findIndex((img) => img.url === url) + 1
      : 0;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
        onClick={(e) => {
          if (e.target === e.currentTarget && !createDraftMutation.isPending) {
            onClose();
          }
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-draft-title"
          className="relative w-full max-w-5xl xl:max-w-6xl max-h-[92vh] overflow-y-auto rounded-3xl border-2 border-herb-600/40 bg-card p-6 shadow-2xl space-y-5 m-auto animate-rise"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-border/80 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-herb-600 text-white shadow-md">
                <ShoppingBag className="h-6 w-6" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-herb-700 block">
                  {subtitle || 'Nouveau Produit · Brouillon WooCommerce'}
                </span>
                <h3 id="modal-draft-title" className="text-lg font-black text-ink">
                  Créer un Brouillon dans WooCommerce
                </h3>
                <p className="text-xs text-ink-soft">
                  Le produit sera créé avec le statut <strong>Brouillon (draft)</strong> sur votre boutique WooCommerce avec le code-barres et les métadonnées.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={createDraftMutation.isPending}
              className="rounded-xl p-1.5 text-ink-ghost hover:bg-cream hover:text-ink cursor-pointer transition-colors"
              title="Fermer (Échap)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {fetchMessage && (
            <div className="rounded-xl border border-sky-300 bg-sky-50 px-3.5 py-2 text-xs font-semibold text-sky-900 flex items-center gap-2 animate-fade">
              <Globe className="h-4 w-4 text-sky-600 shrink-0" />
              <span>{fetchMessage}</span>
            </div>
          )}

          {createDraftMutation.isError && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-600 mt-0.5" />
              <div>
                <p className="font-bold">Erreur de création du brouillon WooCommerce :</p>
                <p className="text-[11px] mt-0.5">{String(createDraftMutation.error?.message || createDraftMutation.error)}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-3.5">
                {/* EAN (GTIN) */}
                <div>
                  <label className="text-xs font-bold text-ink block mb-1">
                    Code-barres EAN (GTIN) :
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={form.ean}
                      onChange={(e) => setForm({ ...form, ean: e.target.value.replace(/\D/g, '') })}
                      placeholder="ex : 3337871324567"
                      className="font-mono font-bold bg-cream/70 text-ink text-sm"
                    />
                    {form.ean ? (
                      <Badge tone="good" className="shrink-0">
                        Code Détecté
                      </Badge>
                    ) : (
                      <Badge tone="neutral" className="shrink-0 text-ink-ghost">
                        Optionnel
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Product Name + source pills */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-ink">
                      {hideContenanceField ? 'Nom du produit' : 'Nom complet du produit'}{' '}
                      <span className="text-rose-500">*</span> :
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          name: toTitleCase(prev.name),
                          brand: prev.brand ? toTitleCase(prev.brand) : prev.brand,
                        }))
                      }
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-herb-700 hover:text-herb-900 transition-colors cursor-pointer"
                      title="Formater en Title case (majuscules initiales sauf liaisons et unités)"
                    >
                      <Sparkles className="h-3 w-3" />
                      <span>Title case</span>
                    </button>
                  </div>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ex : Bioderma Sensibio H2O Solution Micellaire 500ml"
                    className="font-bold text-sm"
                    autoFocus
                    required
                  />
                  {sourcePills(form.nameOptions, form.name, (value) =>
                    setForm((prev) => ({ ...prev, name: value })),
                  )}
                </div>

                {/* Brand and Category (with autocompletion) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    <label className="text-xs font-bold text-ink block mb-1">Marque :</label>
                    <Input
                      value={form.brand}
                      onChange={(e) => {
                        setForm({ ...form, brand: e.target.value });
                        setShowBrandSuggestions(true);
                      }}
                      onFocus={() => setShowBrandSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowBrandSuggestions(false), 200)}
                      placeholder="ex : Bioderma, Uriage, SVR…"
                      autoComplete="off"
                    />
                    {showBrandSuggestions && matchedBrands.length > 0 && (
                      <div className="absolute z-30 mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-border bg-card shadow-lg py-1">
                        {matchedBrands.map((b) => (
                          <button
                            key={b.brand}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-xs text-ink hover:bg-herb-50 hover:text-herb-900 cursor-pointer"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setForm({ ...form, brand: b.brand });
                              setShowBrandSuggestions(false);
                            }}
                          >
                            {b.brand}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="text-xs font-bold text-ink block mb-1">Catégorie :</label>
                    <Input
                      value={form.category}
                      onChange={(e) => {
                        setForm({ ...form, category: e.target.value });
                        setShowCategorySuggestions(true);
                      }}
                      onFocus={() => setShowCategorySuggestions(true)}
                      onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                      placeholder="ex : Soins Visage, Hygiène…"
                      autoComplete="off"
                    />
                    {form.categoryHint !== null && form.category.trim() === '' && (
                      <button
                        type="button"
                        onClick={() =>
                          form.categoryHint !== null &&
                          setForm((prev) => ({ ...prev, category: form.categoryHint ?? '' }))
                        }
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-900 hover:bg-sky-100 transition-colors cursor-pointer"
                        title="Prendre la catégorie suggérée par la source (indicatif)"
                      >
                        Source suggère : {form.categoryHint}
                      </button>
                    )}
                    {showCategorySuggestions && matchedCategories.length > 0 && (
                      <div className="absolute z-30 mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-border bg-card shadow-lg py-1">
                        {matchedCategories.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-xs text-ink hover:bg-herb-50 hover:text-herb-900 cursor-pointer"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setForm({ ...form, category: c.name });
                              setShowCategorySuggestions(false);
                            }}
                          >
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contenance & Regular Price */}
                <div className={`grid grid-cols-1 ${hideContenanceField ? '' : 'sm:grid-cols-2 '}gap-3`}>
                  {!hideContenanceField && (
                    <div>
                      <label className="text-xs font-bold text-ink block mb-1">
                        Contenance (Volume/Poids) :
                      </label>
                      <Input
                        value={form.contenance}
                        onChange={(e) => setForm({ ...form, contenance: e.target.value })}
                        placeholder="ex : 500 ml, 50 g, 30 comprimés"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold text-ink block mb-1">
                      Prix régulier TTC (TND) :
                    </label>
                    <Input
                      value={form.regularPrice}
                      onChange={(e) => setForm({ ...form, regularPrice: e.target.value })}
                      placeholder="ex : 35.500"
                      className="font-mono"
                    />
                  </div>
                </div>

                {/* SKU Reference */}
                <div>
                  <label className="text-xs font-bold text-ink block mb-1">
                    Référence / SKU :
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={form.sku}
                      onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
                      placeholder="Laisser vide (généré automatiquement par WooCommerce)"
                      className="font-mono text-xs"
                    />
                    <span className="text-[11px] text-ink-ghost shrink-0">
                      Optionnel
                    </span>
                  </div>
                  <p className="text-[10px] text-ink-ghost mt-0.5">
                    Recommandé : laisser vide pour que WooCommerce génère automatiquement la référence.
                  </p>
                </div>

                {/* Short Description + source pills */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-bold text-ink">Description courte (Extrait produit) :</label>
                      <button
                        type="button"
                        onClick={handleGetDrakkarsShort}
                        disabled={fetchingSource === 'drakkars-short'}
                        className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-0.5 text-[11px] font-bold text-sky-900 hover:bg-sky-100 transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
                        title="Récupérer ou appliquer la description courte depuis Pharmacie des Drakkars"
                      >
                        {fetchingSource === 'drakkars-short' ? (
                          <RefreshCw className="h-3 w-3 animate-spin text-sky-700" />
                        ) : (
                          <Globe className="h-3 w-3 text-sky-700" />
                        )}
                        <span>Drakkars</span>
                      </button>
                    </div>
                    <span className="text-[11px] text-ink-ghost">Extrait produit (fiche résumé)</span>
                  </div>
                  <textarea
                    rows={2}
                    value={form.shortDescription}
                    onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                    className="w-full rounded-xl border border-border bg-card p-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600 font-sans"
                    placeholder="Description courte ou extrait du produit..."
                  />
                  {sourcePills(form.shortOptions, form.shortDescription, (value) =>
                    setForm((prev) => ({ ...prev, shortDescription: value })),
                  )}
                </div>

                {/* Long Description (HTML) + source pills */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs font-bold text-ink">Description complète (HTML & caractéristiques) :</label>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={handleGetCocoonLong}
                          disabled={fetchingSource === 'cocoon-long'}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-900 hover:bg-amber-100 transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
                          title="Récupérer ou appliquer la description complète HTML depuis Cocoon Center"
                        >
                          {fetchingSource === 'cocoon-long' ? (
                            <RefreshCw className="h-3 w-3 animate-spin text-amber-700" />
                          ) : (
                            <Globe className="h-3 w-3 text-amber-700" />
                          )}
                          <span>Cocoon Center</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleGetBoticinalLong}
                          disabled={fetchingSource === 'boticinal-long'}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-900 hover:bg-emerald-100 transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
                          title="Récupérer ou appliquer la description complète HTML depuis Boticinal"
                        >
                          {fetchingSource === 'boticinal-long' ? (
                            <RefreshCw className="h-3 w-3 animate-spin text-emerald-700" />
                          ) : (
                            <Globe className="h-3 w-3 text-emerald-700" />
                          )}
                          <span>Boticinal</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleGetPharmaGddLong}
                          disabled={fetchingSource === 'pharmagdd-long'}
                          className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-0.5 text-[11px] font-bold text-sky-900 hover:bg-sky-100 transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
                          title="Récupérer ou appliquer la description complète HTML depuis Pharma GDD (Conseils d'utilisation & Composition)"
                        >
                          {fetchingSource === 'pharmagdd-long' ? (
                            <RefreshCw className="h-3 w-3 animate-spin text-sky-700" />
                          ) : (
                            <Globe className="h-3 w-3 text-sky-700" />
                          )}
                          <span>Pharma GDD</span>
                        </button>
                      </div>
                    </div>
                    <span className="text-[11px] text-ink-ghost">Texte complet & caractéristiques</span>
                  </div>
                  <textarea
                    rows={5}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full rounded-xl border border-border bg-card p-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600 font-mono text-[11px]"
                    placeholder="Description complète HTML..."
                  />
                  {sourcePills(
                    form.longOptions,
                    form.description,
                    (value) => setForm((prev) => ({ ...prev, description: value })),
                    true,
                  )}
                </div>
              </div>

              {/* Right 1 Col: Product Photos & Gallery Management */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-ink">
                      Photos du produit :
                    </label>
                    <a
                      href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${form.name || ''} ${form.ean || ''}`.trim())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-herb-700 hover:text-herb-900 font-semibold underline"
                    >
                      <Search className="h-3 w-3" />
                      Google Images ↗
                    </a>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Input
                      value={form.newImageUrl}
                      onChange={(e) => setForm({ ...form, newImageUrl: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddImage(form.newImageUrl);
                        }
                      }}
                      placeholder="Ajouter URL d'image (https://...)"
                      className="text-xs font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => handleAddImage(form.newImageUrl)}
                      disabled={!form.newImageUrl.trim()}
                      className="shrink-0 text-xs px-2.5 py-1.5"
                      title="Ajouter cette photo"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Ajouter
                    </Button>
                  </div>

                  {form.imageOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setImagePickerOpen(true)}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-herb-600/30 bg-herb-50/60 px-3 py-1.5 text-xs font-bold text-herb-900 hover:bg-herb-100 transition-colors cursor-pointer"
                      title="Comparer les packshots de chaque source avec leurs dimensions"
                    >
                      <Eye size={13} />
                      Choisir parmi {form.imageOptions.length} images sources
                    </button>
                  )}
                </div>

                {/* Images list (Main vs Gallery) */}
                <div className="rounded-2xl border border-border bg-paper/60 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                      Images sélectionnées ({form.images.length})
                    </span>
                    <span className="text-[10px] text-ink-ghost">
                      1re = Packshot, reste = Galerie
                    </span>
                  </div>

                  {form.images.length > 0 ? (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {form.images.map((img) => (
                        <div
                          key={img.id}
                          className={`flex items-center gap-2.5 p-2 rounded-xl border transition-all ${
                            img.isMain
                              ? 'bg-herb-50/80 border-herb-600/50 shadow-2xs'
                              : 'bg-card border-border hover:border-border/80'
                          }`}
                        >
                          <img
                            src={img.url}
                            alt="Packshot"
                            className="h-12 w-12 rounded-lg object-contain bg-white border border-border/80 p-1 shrink-0"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="1.5"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                            }}
                          />

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              {img.isMain ? (
                                <Badge tone="good" className="text-[10px] font-bold py-0 px-1.5 flex items-center gap-0.5">
                                  <Star className="h-2.5 w-2.5 fill-current" /> Principale
                                </Badge>
                              ) : (
                                <Badge tone="neutral" className="text-[10px] py-0 px-1.5">
                                  Galerie
                                </Badge>
                              )}
                              {dimBadge(img.url)}
                            </div>
                            <p className="text-[10px] font-mono text-ink-ghost truncate mt-0.5" title={img.url}>
                              {img.url}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {!img.isMain && (
                              <button
                                type="button"
                                onClick={() => handleSetMainImage(img.id)}
                                className="px-2 py-1 rounded-lg text-[10px] font-semibold text-herb-700 hover:bg-herb-100 hover:text-herb-900 transition-colors cursor-pointer"
                                title="Définir comme photo principale"
                              >
                                Définir Principale
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(img.id)}
                              className="p-1 rounded-lg text-ink-ghost hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                              title="Supprimer cette image"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-28 rounded-xl bg-card border border-dashed border-border flex flex-col items-center justify-center text-xs text-ink-ghost gap-1">
                      <ImageIcon className="h-7 w-7 text-ink-ghost/40" />
                      <span>Aucune photo sélectionnée</span>
                      <span className="text-[10px]">Collez une URL ci-dessus pour ajouter</span>
                    </div>
                  )}
                </div>

                {/* WooCommerce Rules Checklist */}
                <div className="rounded-2xl border border-herb-600/30 bg-herb-50/40 p-3 space-y-1.5 text-xs">
                  <span className="font-bold text-herb-900 block">Règles d'injection Woo :</span>
                  <ul className="space-y-1 text-ink-soft text-[11px]">
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>
                        Statut : <strong>Brouillon (draft)</strong>
                      </span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>Photo principale + photos galerie transmises</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>Alt Text, Titre, Légende & Description = Nom produit</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>Marque & Catégorie auto-recherchées et liées</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>Code EAN dans <code>global_unique_id</code> & <code>alg_ean</code></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-border flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="soft"
                onClick={onClose}
                disabled={createDraftMutation.isPending}
              >
                Annuler
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={createDraftMutation.isPending || !form.name.trim()}
                className="font-bold shadow-md cursor-pointer bg-herb-700 hover:bg-herb-800 text-white"
              >
                {createDraftMutation.isPending ? (
                  <>
                    <RefreshCw size={14} className="animate-spin mr-1.5" />
                    Création sur WooCommerce…
                  </>
                ) : (
                  <>
                    <ShoppingBag size={14} className="mr-1.5" />
                    Créer le brouillon dans WooCommerce
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Nested source image picker — floats above this modal */}
      {imagePickerOpen && (
        <div
          className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
          onClick={() => setImagePickerOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Choisir le packshot"
            className="relative w-full max-w-3xl rounded-3xl border border-border bg-card p-6 shadow-2xl max-h-[92vh] overflow-y-auto m-auto animate-rise"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setImagePickerOpen(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-ink-ghost hover:bg-cream hover:text-ink transition-colors cursor-pointer"
              title="Fermer (Échap)"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-base font-bold text-ink">Choisir le packshot</h3>
            <p className="text-xs text-ink-soft mt-0.5">
              Un visuel par source — dimensions mesurées en direct. Minimum recommandé :{' '}
              <strong>{MIN_IMAGE_PX} × {MIN_IMAGE_PX} px</strong>. Cliquez l'image pour la
              prendre comme <strong>principale</strong>, ou ajoutez-la à la{' '}
              <strong>galerie</strong>.
            </p>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(() => {
                const rank = (url: string): number => {
                  const d = imageDims[url];
                  if (d === undefined) return 1;
                  if (d === 'error') return 0;
                  return d.w >= MIN_IMAGE_PX && d.h >= MIN_IMAGE_PX ? 2 : 0;
                };
                return [...form.imageOptions]
                  .sort((a, b) => rank(b.url) - rank(a.url))
                  .map((o) => {
                    const current = form.images.find((img) => img.url === o.url);
                    const isMain = current?.isMain ?? false;
                    const galleryIdx = galleryOrder(o.url);
                    return (
                      <div
                        key={o.url}
                        className={
                          isMain
                            ? 'rounded-2xl border-2 border-herb-600 bg-herb-50/50 p-2.5 text-center shadow-xs'
                            : current
                              ? 'rounded-2xl border-2 border-sky-500 bg-sky-50/40 p-2.5 text-center shadow-xs'
                              : 'rounded-2xl border border-border bg-paper/60 p-2.5 text-center hover:border-herb-600/50 transition-all'
                        }
                        title={o.url}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const id = ensureImage(o.url);
                            if (id) handleSetMainImage(id);
                            setImagePickerOpen(false);
                          }}
                          className="block w-full cursor-pointer"
                          title="Prendre comme image principale et fermer"
                        >
                          <img
                            src={o.url}
                            alt={o.source}
                            className="h-28 w-full rounded-xl object-contain bg-white border border-border p-1"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        </button>
                        <span className="mt-1.5 block text-[11px] font-bold text-ink truncate">
                          {o.source}
                        </span>
                        {dimBadge(o.url)}
                        <div className="mt-1.5 flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const id = ensureImage(o.url);
                              if (id) handleSetMainImage(id);
                            }}
                            className={
                              isMain
                                ? 'rounded-full bg-herb-700 text-white px-2 py-0.5 text-[11px] font-bold cursor-pointer'
                                : 'rounded-full border border-herb-600/30 px-2 py-0.5 text-[11px] font-semibold text-herb-900 hover:bg-herb-100 cursor-pointer'
                            }
                            title="Définir comme image principale (reste ouvert)"
                          >
                            ★ {isMain ? 'Principale' : 'Main'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const existing = form.images.find((img) => img.url === o.url);
                              if (existing) {
                                if (!existing.isMain) handleRemoveImage(existing.id);
                              } else {
                                ensureImage(o.url);
                              }
                            }}
                            disabled={isMain}
                            className={
                              current && !isMain
                                ? 'rounded-full bg-sky-600 text-white px-2 py-0.5 text-[11px] font-bold cursor-pointer'
                                : 'rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-ink-soft hover:bg-cream cursor-pointer disabled:opacity-40'
                            }
                            title={
                              current && !isMain
                                ? 'Retirer de la galerie'
                                : isMain
                                  ? 'Image principale (toujours envoyée en premier)'
                                  : 'Ajouter à la galerie (reste ouvert)'
                            }
                          >
                            {current && !isMain ? `✓ Galerie #${galleryIdx}` : '+ Galerie'}
                          </button>
                        </div>
                      </div>
                    );
                  });
              })()}
            </div>

            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-3">
              <span className="text-xs text-ink-soft">
                {form.images.filter((img) => !img.isMain).length === 0
                  ? 'Aucune image en galerie — la principale seule sera envoyée.'
                  : `${form.images.filter((img) => !img.isMain).length} image(s) en galerie après la principale.`}
              </span>
              <Button variant="primary" size="sm" onClick={() => setImagePickerOpen(false)} className="font-bold">
                Valider
              </Button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
